/* eslint-disable */
// src/tables/visitStrategies/TragamonedaStrategy.js

import React from "react";
import BaseStrategy from "./BaseStrategy";
import { calculateFormula } from "layouts/visitMachine/strategies/calculateFormula";
import { hasRent } from "utils/rent";
import { isInsertedInCurrentVisitNoticeRow } from "utils/visitCollectionState";
import { roundForDisplay } from "utils/formatters";
import { getAccumulationModeLabel } from "utils/accumulationModeLabels";
import { getMachineFormula, hasMachineOperatorFormula } from "utils/machineFormula";
import AutoGrowingNoteTextarea from "components/visit/AutoGrowingNoteTextarea";

export default class TragamonedaStrategy extends BaseStrategy {
  accumulatedWarningText = "El valor actual debería ser igual o mayor al anterior.";
  pick = (m, pascal, camel) => m?.[pascal] ?? m?.[camel];
  getFormula = (m) => getMachineFormula(m);
  usesFormula = (m) => hasMachineOperatorFormula(m);
  toNumberOrNull = (v) => {
    if (v == null || v === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  normalizeMoneyForDisplay = (value) => {
    const n = this.toNumberOrNull(value);
    return n == null ? null : roundForDisplay(n);
  };
  getDisplayMoneyDifference({ officialMoney, counterMoney, fallbackDifference = null }) {
    const normalizedOfficial = this.normalizeMoneyForDisplay(officialMoney);
    const normalizedCounter = this.normalizeMoneyForDisplay(counterMoney);
    if (normalizedOfficial != null && normalizedCounter != null) {
      return normalizedOfficial - normalizedCounter;
    }
    return this.normalizeMoneyForDisplay(fallbackDifference);
  }
  pickNumeric = (m, ...keys) => this.pickNumber(m, ...keys);
  pickExplicitPreviousNet = (last, machine, key) =>
    Object.prototype.hasOwnProperty.call(last || {}, key) ? last[key] : machine?.[key];
  pickLiveNumeric = (m, camelKey, pascalKey) =>
    this.isEdit
      ? this.pickNumeric(m, camelKey, pascalKey)
      : this.pickNumeric(m, pascalKey, camelKey);
  pickLiveCredits = (m, camelKey, pascalKey) =>
    this.isEdit
      ? this.pickNumeric(m, camelKey, pascalKey)
      : this.pickNumeric(m, pascalKey, camelKey);
  creditFmt = (val, pendingText = "-") => {
    const n = this.toNumberOrNull(val);
    return n == null
      ? pendingText
      : this.nf.format(n);
  };
  isInputFieldSettled(key, field) {
    return this.settledInputFields?.[key]?.[field] === true;
  }
  shouldShowAccumulatedValueWarning({ key, field, currentValue, previousValue, isCollected }) {
    if (!isCollected) return false;
    if (!this.isInputFieldSettled(key, field)) return false;
    const current = this.toNumberOrNull(currentValue);
    const previous = this.toNumberOrNull(previousValue);
    if (current == null || previous == null) return false;
    return current < previous;
  }

  getCounterContext(key) {
    const context = this.counterVerificationContextsByLocalMachineId?.[Number(key)];
    return context?.hasCounterConfig === true ? context : null;
  }

  getCounterMode(key) {
    return this.userInputs?.[key]?.CounterMode || "verify";
  }

  applyCounterFormula(netCounter, formula) {
    if (formula == null) return { value: netCounter, error: null };

    const type = String(formula?.Type || "").trim();
    const value = Number(formula?.Value);
    if (!Number.isFinite(value)) return { value: null, error: "Fórmula de contador inválida" };
    if (type === "/") {
      if (value === 0) return { value: null, error: "Fórmula de contador inválida" };
      return { value: netCounter / value, error: null };
    }
    if (type === "*") return { value: netCounter * value, error: null };
    return { value: null, error: "Fórmula de contador inválida" };
  }

  formatCounterFormula(formula) {
    if (formula == null) return "Sin fórmula";

    const type = String(formula?.Type || "").trim();
    const value = this.toNumberOrNull(formula?.Value);
    if (!["*", "/"].includes(type) || value == null) return "-";
    return `${type} ${this.nf.format(value)}`;
  }

  getCounterFormulaInfo(context) {
    const inText = this.formatCounterFormula(context?.config?.CounterInFormula);
    const outText = this.formatCounterFormula(context?.config?.CounterOutFormula);
    return {
      inText,
      outText,
      hasSameFormula: inText === outText,
    };
  }

  getOfficialCounterMoney(m, key = null) {
    if (key != null && this.isEdit) {
      const inputs = this.userInputs?.[key] || {};
      const hasInput = (field) => {
        const value = inputs?.[field];
        if (value == null) return false;
        if (typeof value === "string") return value.trim() !== "";
        return true;
      };
      const hasFormula = this.usesFormula(m);
      const isAccumulative = Boolean(m?.IsAccumulative);
      const hasOfficialIn = hasFormula
        ? isAccumulative
          ? hasInput("CreditsIn")
          : hasInput("NetCreditsIn")
        : isAccumulative
          ? hasInput("MoneyIn")
          : hasInput("NetMoneyIn");
      const hasOfficialOut = hasFormula
        ? isAccumulative
          ? hasInput("CreditsOut")
          : hasInput("NetCreditsOut")
        : isAccumulative
          ? hasInput("MoneyOut")
          : hasInput("NetMoneyOut");
      return {
        OfficialMoneyIn: hasOfficialIn ? this.toNumberOrNull(m?.netMoneyIn ?? m?.NetMoneyIn) : null,
        OfficialMoneyOut: hasOfficialOut
          ? this.toNumberOrNull(m?.netMoneyOut ?? m?.NetMoneyOut)
          : null,
      };
    }

    const inValue =
      this.toNumberOrNull(m?.netMoneyIn ?? m?.NetMoneyIn) ??
      this.toNumberOrNull(m?.moneyIn ?? m?.MoneyIn);
    const outValue =
      this.toNumberOrNull(m?.netMoneyOut ?? m?.NetMoneyOut) ??
      this.toNumberOrNull(m?.moneyOut ?? m?.MoneyOut);
    return { OfficialMoneyIn: inValue, OfficialMoneyOut: outValue };
  }

  getAccumulatedVerificationContext(context) {
    const accumulated = context?.accumulatedVerification;
    return accumulated?.isAccumulated === true ? accumulated : null;
  }

  formatIncludedVisitIds(ids = []) {
    const values = Array.isArray(ids) ? ids.filter((id) => id != null) : [];
    if (!values.length) return null;
    const visible = values.slice(0, 5).join(", ");
    return values.length > 5 ? `${visible}, ...` : visible;
  }

  formatAccumulatedVisitDates(dates = []) {
    const values = Array.isArray(dates) ? dates.filter(Boolean) : [];
    if (!values.length) return null;
    const currentYear = new Date().getFullYear();
    const formatted = values
      .slice(0, 5)
      .map((dateLike) => {
        const date = new Date(dateLike);
        if (Number.isNaN(date.getTime())) return null;
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return year === currentYear ? `${day}/${month}` : `${day}/${month}/${year}`;
      })
      .filter(Boolean);
    if (!formatted.length) return null;
    return values.length > 5 ? `${formatted.join(", ")}, ...` : formatted.join(", ");
  }

  buildAccumulatedCounterText({ count, datesText }) {
    const parsedCount = Number(count);
    if (!Number.isFinite(parsedCount) || parsedCount <= 0) return null;
    if (parsedCount === 1) {
      return "Incluye 1 recaudación anterior sin lectura de contadores.";
    }
    const datesPart = datesText ? `: ${datesText}` : "";
    return `Incluye ${parsedCount} recaudaciones anteriores sin lectura de contadores${datesPart}.`;
  }

  formatShortDate(dateLike) {
    const date = new Date(dateLike);
    if (Number.isNaN(date.getTime())) return null;
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return year === new Date().getFullYear() ? `${day}/${month}` : `${day}/${month}/${year}`;
  }

  renderAccumulatedComparisonBreakdown({
    currentOfficialMoney,
    accumulatedOfficialMoney,
    includedVisitBreakdown = [],
    side,
    totalOfficialMoney,
    counterMoney,
    difference,
  }) {
    const current = this.toNumberOrNull(currentOfficialMoney);
    const accumulated = this.toNumberOrNull(accumulatedOfficialMoney);
    const total = this.toNumberOrNull(totalOfficialMoney);
    const counter = this.toNumberOrNull(counterMoney);
    const diff = this.toNumberOrNull(difference);
    if (current == null || accumulated == null || total == null) return null;

    const money = (value) => this.moneyFmt.format(value);
    const sideKey = side === "out" ? "OfficialMoneyOut" : "OfficialMoneyIn";
    const breakdown = Array.isArray(includedVisitBreakdown) ? includedVisitBreakdown : [];
    const visibleBreakdown = this.individualEditorLayout ? breakdown : breakdown.slice(0, 5);
    const count = breakdown.length || 1;
    const visibleDates = visibleBreakdown
      .map((item) => this.formatShortDate(item?.VisitDateTime))
      .filter(Boolean);
    const datesText =
      visibleDates.length > 0
        ? `${visibleDates.join(", ")}${!this.individualEditorLayout && breakdown.length > 5 ? ", ..." : ""}`
        : null;
    const introText = this.individualEditorLayout
      ? `Incluye ${count === 1 ? "recaudación" : "recaudaciones"} sin verificar${datesText ? `: ${datesText}` : ""}`
      : count === 1
        ? `Compara la recaudación actual + la recaudación anterior sin lectura de contadores${
            datesText ? `: ${datesText}` : ""
          }.`
        : `Compara la recaudación actual + las ${count} recaudaciones anteriores sin lectura de contadores${
            datesText ? `: ${datesText}` : ""
          }.`;

    const lineStyle = {
      fontSize: this.individualEditorLayout ? 16 : 12,
      lineHeight: 1.25,
      color: "#495057",
      whiteSpace: "normal",
      overflowWrap: "anywhere",
    };
    const mutedLineStyle = { ...lineStyle, opacity: 0.86 };

    if (count === 1) {
      const single = breakdown[0] || null;
      const dateText = this.formatShortDate(single?.VisitDateTime);
      if (this.individualEditorLayout) {
        return (
          <div style={{ display: "grid", gap: 2, marginTop: -2 }}>
            <div style={lineStyle}>{introText}</div>
            <div style={mutedLineStyle}>Actual: {money(current)}</div>
            <div style={mutedLineStyle}>
              {dateText || "Anterior"}: {money(accumulated)}
            </div>
            <div style={{ ...lineStyle, fontWeight: 700 }}>
              Total oficial acumulado: {money(total)}
            </div>
          </div>
        );
      }
      return (
        <div style={{ display: "grid", gap: 2, marginTop: -2 }}>
          <div style={lineStyle}>{introText}</div>
          <div style={lineStyle}>Comparacion:</div>
          <div style={mutedLineStyle}>
            Actual {money(current)} + anterior{dateText ? ` ${dateText}` : ""} {money(accumulated)}{" "}
            = {money(total)}
          </div>
          {counter != null && diff != null && (
            <div style={lineStyle}>
              Total recaudaciones {money(total)} - contador {money(counter)} = diferencia{" "}
              {money(diff)}
            </div>
          )}
        </div>
      );
    }

    if (this.individualEditorLayout) {
      return (
        <div style={{ display: "grid", gap: 2, marginTop: -2 }}>
          <div style={lineStyle}>{introText}</div>
          <div style={mutedLineStyle}>Actual: {money(current)}</div>
          {visibleBreakdown.map((item, index) => {
            const dateText = this.formatShortDate(item?.VisitDateTime);
            const value = this.toNumberOrNull(item?.[sideKey]);
            return (
              <div key={`${sideKey}-${item?.VisitMachineId ?? index}`} style={mutedLineStyle}>
                {dateText || `Anterior ${index + 1}`}: {value == null ? "-" : money(value)}
              </div>
            );
          })}
          <div style={{ ...lineStyle, fontWeight: 700 }}>
            Total oficial acumulado: {money(total)}
          </div>
        </div>
      );
    }

    return (
      <div style={{ display: "grid", gap: 2, marginTop: -2 }}>
        <div style={lineStyle}>{introText}</div>
        <div style={lineStyle}>Comparacion:</div>
        <div style={mutedLineStyle}>Actual: {money(current)}</div>
        {visibleBreakdown.map((item, index) => {
          const dateText = this.formatShortDate(item?.VisitDateTime);
          const value = this.toNumberOrNull(item?.[sideKey]);
          return (
            <div key={`${sideKey}-${item?.VisitMachineId ?? index}`} style={mutedLineStyle}>
              Anterior{dateText ? ` ${dateText}` : ""} {value == null ? "-" : money(value)}
            </div>
          );
        })}
        {breakdown.length > 5 && <div style={mutedLineStyle}>...</div>}
        <div style={lineStyle}>Total recaudaciones {money(total)}</div>
        {counter != null && diff != null && (
          <div style={lineStyle}>
            - contador {money(counter)} = diferencia {money(diff)}
          </div>
        )}
      </div>
    );
  }

  renderAccumulatedCollectionRows({ includedVisitBreakdown = [], side }) {
    if (!this.individualEditorLayout || !Array.isArray(includedVisitBreakdown)) return [];
    const sideKey = side === "out" ? "OfficialMoneyOut" : "OfficialMoneyIn";
    return includedVisitBreakdown.map((item, index) => {
      const dateText = this.formatShortDate(item?.VisitDateTime) || "fecha desconocida";
      const value = this.toNumberOrNull(item?.[sideKey]);
      return React.cloneElement(
        this.counterLine(
          `Sin verificar del ${dateText}`,
          value == null ? "-" : this.moneyFmt.format(value)
        ),
        { key: `unverified-${sideKey}-${item?.VisitMachineId ?? index}` }
      );
    });
  }

  renderCollectionDetailAccumulatedRows({ includedVisitBreakdown = [], side }) {
    if (
      !this.collectionDetailPresentation ||
      !this.isRead ||
      !Array.isArray(includedVisitBreakdown)
    ) {
      return [];
    }
    const sideKey = side === "out" ? "OfficialMoneyOut" : "OfficialMoneyIn";
    return includedVisitBreakdown.map((item, index) => {
      const dateText = this.formatShortDate(item?.VisitDateTime) || "fecha desconocida";
      const value = this.toNumberOrNull(item?.[sideKey]);
      return React.cloneElement(
        this.counterLine(
          `Sin verificar del ${dateText}`,
          value == null ? "-" : this.moneyFmt.format(value)
        ),
        { key: `detail-unverified-${sideKey}-${item?.VisitMachineId ?? index}` }
      );
    });
  }

  counterGroupTitle(label) {
    if (!this.individualEditorLayout) return null;
    return (
      <div
        style={{
          marginTop: 4,
          fontSize: "inherit",
          fontWeight: 700,
          lineHeight: 1.25,
        }}
      >
        {label}
      </div>
    );
  }

  calculateCounterPreview({ key, m, context }) {
    if (!context?.previousBase || !context?.config)
      return { message: "Contexto de contadores incompleto." };
    if (context.nextVerifyBehavior === "take_new_base_after_not_read") {
      return {
        mode: "take_new_base_after_not_read",
        message:
          "Esta lectura se tomará como nuevo punto de partida porque la visita anterior quedó sin verificar.",
      };
    }

    const currentIn = this.toNumberOrNull(this.userInputs?.[key]?.CounterCurrentIn);
    const currentOut = this.toNumberOrNull(this.userInputs?.[key]?.CounterCurrentOut);
    const accumulated = this.getAccumulatedVerificationContext(context);
    const baseIn = accumulated
      ? this.toNumberOrNull(accumulated.baseCounterIn)
      : this.toNumberOrNull(context.previousBase.CounterIn);
    const baseOut = accumulated
      ? this.toNumberOrNull(accumulated.baseCounterOut)
      : this.toNumberOrNull(context.previousBase.CounterOut);
    if (baseIn == null || baseOut == null) {
      return {
        message: accumulated
          ? "Base acumulada de contadores incompleta."
          : "Base anterior de contadores incompleta.",
      };
    }

    const previewFormulaInfo = this.getCounterFormulaInfo(context);
    const officialMoney = this.getOfficialCounterMoney(m, key);
    const previewOfficialMoneyIn = officialMoney.OfficialMoneyIn;
    const previewOfficialMoneyOut = officialMoney.OfficialMoneyOut;
    const previewPersistedAccumulatedOfficialMoneyIn = accumulated
      ? this.toNumberOrNull(accumulated.accumulatedOfficialMoneyIn) ?? 0
      : null;
    const previewPersistedAccumulatedOfficialMoneyOut = accumulated
      ? this.toNumberOrNull(accumulated.accumulatedOfficialMoneyOut) ?? 0
      : null;
    const previewOfficialMoneyInForComparison =
      accumulated && previewOfficialMoneyIn != null
        ? previewPersistedAccumulatedOfficialMoneyIn + previewOfficialMoneyIn
        : previewOfficialMoneyIn;
    const previewOfficialMoneyOutForComparison =
      accumulated && previewOfficialMoneyOut != null
        ? previewPersistedAccumulatedOfficialMoneyOut + previewOfficialMoneyOut
        : previewOfficialMoneyOut;
    const previewTolerance = this.toNumberOrNull(context.toleranceAmount) ?? 10;
    const calculateCounterSide = ({ current, base, formula, officialMoney }) => {
      if (current == null || base == null) {
        return { net: null, counterMoney: null, difference: null, isOk: null, error: null };
      }
      const net = current - base;
      if (net < 0) {
        return {
          net,
          counterMoney: null,
          difference: null,
          isOk: null,
          error: "Los contadores actuales deben ser mayores o iguales a la base anterior.",
        };
      }
      const moneyResult = this.applyCounterFormula(net, formula);
      if (moneyResult.error) {
        return { net, counterMoney: null, difference: null, isOk: null, error: moneyResult.error };
      }
      const displayDifference = this.getDisplayMoneyDifference({
        officialMoney,
        counterMoney: moneyResult.value,
      });
      const difference = officialMoney == null ? null : Number(officialMoney) - moneyResult.value;
      return {
        net,
        counterMoney: moneyResult.value,
        difference,
        displayDifference,
        isOk: displayDifference == null ? null : Math.abs(displayDifference) <= previewTolerance,
        error: null,
      };
    };
    const inSide = calculateCounterSide({
      current: currentIn,
      base: baseIn,
      formula: context.config.CounterInFormula,
      officialMoney: previewOfficialMoneyInForComparison,
    });
    const outSide = calculateCounterSide({
      current: currentOut,
      base: baseOut,
      formula: context.config.CounterOutFormula,
      officialMoney: previewOfficialMoneyOutForComparison,
    });
    const hasCompleteCounterComparison =
      inSide.displayDifference != null &&
      outSide.displayDifference != null &&
      inSide.isOk != null &&
      outSide.isOk != null;

    {
      const isVerificationOk = hasCompleteCounterComparison ? inSide.isOk && outSide.isOk : null;
      const isExact =
        hasCompleteCounterComparison &&
        inSide.displayDifference === 0 &&
        outSide.displayDifference === 0;
      return {
        mode: accumulated ? "verify_accumulated_after_not_read" : "verify",
        isAccumulated: Boolean(accumulated),
        formulaInfo: previewFormulaInfo,
        statusText: hasCompleteCounterComparison
          ? isExact
            ? "Correcto"
            : isVerificationOk
              ? "Dentro de tolerancia"
              : "Incorrecto"
          : null,
        statusColor: hasCompleteCounterComparison && isVerificationOk ? "#1b7f3a" : "#b31d28",
        message:
          previewOfficialMoneyIn == null || previewOfficialMoneyOut == null
            ? "Completa primero la entrada/salida de la máquina."
            : inSide.error || outSide.error || null,
        OfficialMoneyIn: previewOfficialMoneyInForComparison,
        OfficialMoneyOut: previewOfficialMoneyOutForComparison,
        CurrentOfficialMoneyIn: previewOfficialMoneyIn,
        CurrentOfficialMoneyOut: previewOfficialMoneyOut,
        PersistedAccumulatedOfficialMoneyIn: previewPersistedAccumulatedOfficialMoneyIn,
        PersistedAccumulatedOfficialMoneyOut: previewPersistedAccumulatedOfficialMoneyOut,
        CounterMoneyIn: inSide.counterMoney,
        CounterMoneyOut: outSide.counterMoney,
        DifferenceMoneyIn: inSide.displayDifference,
        DifferenceMoneyOut: outSide.displayDifference,
        RawDifferenceMoneyIn: inSide.difference,
        RawDifferenceMoneyOut: outSide.difference,
        BaseCounterIn: baseIn,
        BaseCounterOut: baseOut,
        CurrentCounterIn: currentIn,
        CurrentCounterOut: currentOut,
        NetCounterIn: inSide.net,
        NetCounterOut: outSide.net,
        includedVisitCount: accumulated?.includedVisitCount ?? null,
        includedVisitIds: accumulated?.includedVisitIds ?? [],
        includedVisitDates: accumulated?.includedVisitDates ?? [],
        includedVisitBreakdown: accumulated?.includedVisitBreakdown ?? [],
        tolerance: previewTolerance,
      };
    }
  }

  counterDisclosureBlock(children, { centered = false } = {}) {
    if (!children) return null;
    const items = Array.isArray(children) ? children.filter(Boolean) : [children];
    if (!items.length) return null;
    return (
      <div
        style={{
          display: "grid",
          gap: 6,
          fontSize: "inherit",
          justifyItems: centered ? "center" : undefined,
          textAlign: centered ? "center" : undefined,
          width: centered ? "100%" : undefined,
        }}
      >
        {React.Children.toArray(items)}
      </div>
    );
  }

  fieldLine(label, value, { color = null, boldValue = false, fontSize = null } = {}) {
    if (value == null || value === "") return null;
    return (
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 10,
          lineHeight: 1.25,
          fontSize: fontSize || undefined,
          width: this.individualEditorLayout ? "100%" : undefined,
        }}
      >
        <span
          style={{
            opacity: 0.72,
            minWidth: 0,
            flex: this.individualEditorLayout ? "1 1 auto" : undefined,
            overflowWrap: this.individualEditorLayout ? "anywhere" : undefined,
          }}
        >
          {label}:
        </span>
        <span
          style={{
            fontWeight: boldValue ? 700 : 400,
            color: color || undefined,
            textAlign: "right",
            minWidth: this.individualEditorLayout ? "max-content" : 0,
            flex: this.individualEditorLayout ? "0 0 auto" : undefined,
            whiteSpace: this.individualEditorLayout ? "nowrap" : undefined,
          }}
        >
          {value}
        </span>
      </div>
    );
  }

  inputFieldLine(label, inputNode) {
    if (!inputNode) return null;
    return (
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 10,
          width: "100%",
        }}
      >
        <span style={{ minWidth: 0 }}>{label}</span>
        <span style={{ display: "inline-flex", justifyContent: "flex-end", minWidth: 0 }}>
          {inputNode}
        </span>
      </div>
    );
  }

  counterLine(label, value, options = {}) {
    return this.fieldLine(label, value, options);
  }

  isCounterDifferenceOutsideTolerance(difference, tolerance) {
    const diff = this.toNumberOrNull(difference);
    const toleranceValue = this.toNumberOrNull(tolerance);
    if (diff == null || toleranceValue == null) return false;
    return Math.abs(diff) > toleranceValue;
  }

  counterDifferenceColor(isOutsideTolerance) {
    return isOutsideTolerance ? this.negStyle(-1).color || this.NEG_COLOR : null;
  }

  getCounterDifferenceQualification(difference, tolerance) {
    const diff = this.toNumberOrNull(difference);
    if (diff == null) return null;
    if (diff === 0) return { label: "Correcto", color: "#1b7f3a" };
    const toleranceValue = this.toNumberOrNull(tolerance);
    if (toleranceValue == null) return null;
    return Math.abs(diff) <= toleranceValue
      ? { label: "Dentro de tolerancia", color: "#1b7f3a" }
      : { label: "Incorrecto", color: this.NEG_COLOR };
  }

  counterDifferenceLine(label, difference, formatter, tolerance) {
    const value = difference == null ? "Pendiente" : formatter(difference);
    if (this.collectionDetailPresentation) {
      const qualification = this.getCounterDifferenceQualification(difference, tolerance);
      const qualificationLabel =
        qualification?.label === "Dentro de tolerancia" ? "En rango" : qualification?.label;
      return (
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: 10,
            lineHeight: 1.25,
            width: "100%",
          }}
        >
          <span style={{ opacity: 0.72, minWidth: 0 }}>{label}:</span>
          <span
            style={{
              display: "inline-flex",
              justifyContent: "flex-end",
              alignItems: "baseline",
              flexWrap: "wrap",
              gap: 6,
              minWidth: 0,
              textAlign: "right",
            }}
          >
            {qualification && (
              <span style={{ fontStyle: "normal", fontWeight: 400 }}>
                {qualificationLabel}
              </span>
            )}
            <span
              style={{
                color: this.counterDifferenceColor(
                  this.isCounterDifferenceOutsideTolerance(difference, tolerance)
                ),
                fontWeight: 700,
              }}
            >
              {value}
            </span>
          </span>
        </div>
      );
    }
    if (!this.individualEditorLayout) {
      return this.counterLine(label, value, {
        boldValue: true,
        color: this.counterDifferenceColor(
          this.isCounterDifferenceOutsideTolerance(difference, tolerance)
        ),
      });
    }
    const qualification = this.getCounterDifferenceQualification(difference, tolerance);
    return (
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 10,
          lineHeight: 1.25,
        }}
      >
        <span style={{ opacity: 0.72, minWidth: 0, fontWeight: 700 }}>{label}:</span>
        <span
          style={{
            display: "inline-flex",
            justifyContent: "flex-end",
            alignItems: "baseline",
            flexWrap: "wrap",
            gap: 6,
            minWidth: 0,
            textAlign: "right",
          }}
        >
          {qualification && (
            <span style={{ color: qualification.color, fontWeight: 700 }}>
              {qualification.label === "Dentro de tolerancia" ? "En rango" : qualification.label}
            </span>
          )}
          <span style={{ color: qualification?.color, fontWeight: 700 }}>{value}</span>
        </span>
      </div>
    );
  }

  counterParagraph(text) {
    if (text == null || text === "") return null;
    return (
      <div
        style={{
          display: "block",
          whiteSpace: "normal",
          overflowWrap: "anywhere",
          wordBreak: "break-word",
          lineHeight: 1.35,
          maxWidth: "100%",
          fontSize: "inherit",
          fontWeight: 600,
          color: "#495057",
        }}
      >
        {text}
      </div>
    );
  }

  counterStatusBadge(label, status = "default") {
    const colors =
      {
        success: {
          color: "#1b7f3a",
          background: "rgba(27, 127, 58, 0.08)",
          border: "rgba(27, 127, 58, 0.25)",
        },
        error: {
          color: "#b31d28",
          background: "rgba(179, 29, 40, 0.08)",
          border: "rgba(179, 29, 40, 0.25)",
        },
        warning: {
          color: "#8a4b00",
          background: "rgba(255, 193, 7, 0.14)",
          border: "rgba(138, 75, 0, 0.2)",
        },
        default: {
          color: "#5f6368",
          background: "rgba(95, 99, 104, 0.08)",
          border: "rgba(95, 99, 104, 0.18)",
        },
      }[status] || {};
    return (
      <span
        style={{
          display: "inline-flex",
          width: "fit-content",
          padding: "3px 8px",
          borderRadius: 999,
          border: `1px solid ${colors.border}`,
          color: colors.color,
          background: colors.background,
          fontSize: this.individualEditorLayout ? 16 : 12,
          fontWeight: 800,
          lineHeight: 1.2,
        }}
      >
        {label}
      </span>
    );
  }

  getCounterReadFormulaInfo(verification = {}) {
    const formatFormula = (formula) => {
      const hasNoFormula =
        formula == null || (formula?.Id == null && formula?.Type == null && formula?.Value == null);
      if (hasNoFormula) return this.collectionDetailPresentation ? "$" : "Sin fórmula";
      const type = String(formula?.Type ?? "").trim();
      const value = this.toNumberOrNull(formula?.Value);
      if (!["*", "/"].includes(type) || value == null)
        return formula?.Id != null
          ? `${this.individualEditorLayout ? "Fórmula" : "Formula"} #${formula.Id}`
          : "-";
      return `${type} ${this.nf.format(value)}`;
    };
    const inFormula = {
      Id: verification.CounterInFormula?.Id ?? verification.CounterInFormulaId,
      Type: verification.CounterInFormula?.Type ?? verification.CounterInFormulaType,
      Value: verification.CounterInFormula?.Value ?? verification.CounterInFormulaValue,
    };
    const outFormula = {
      Id: verification.CounterOutFormula?.Id ?? verification.CounterOutFormulaId,
      Type: verification.CounterOutFormula?.Type ?? verification.CounterOutFormulaType,
      Value: verification.CounterOutFormula?.Value ?? verification.CounterOutFormulaValue,
    };
    const inText = formatFormula(inFormula);
    const outText = formatFormula(outFormula);
    return { inText, outText, hasSameFormula: inText === outText };
  }

  getCounterReadDisplay(m) {
    const verification = m?.CounterVerification ?? m?.counterVerification ?? null;
    if (!verification?.Status) return null;
    const status = String(verification.Status || "");
    const reason =
      verification.UnverifiedReason == null ? null : String(verification.UnverifiedReason);
    const isVerified = ["verified_exact", "verified_with_tolerance", "verified_incorrect"].includes(
      status
    );
    const statusText =
      {
        verified_exact: "Correcto",
        verified_with_tolerance: "Dentro de tolerancia",
        verified_incorrect: "Incorrecto",
        unverified: "No verificado",
      }[status] || status;
    const badgeStatus =
      status === "verified_incorrect" ? "error" : isVerified ? "success" : "warning";
    const toNum = (value) => this.toNumberOrNull(value);
    const currentOfficialIn =
      toNum(m?.NetMoneyIn ?? m?.netMoneyIn) ?? toNum(m?.MoneyIn ?? m?.moneyIn);
    const currentOfficialOut =
      toNum(m?.NetMoneyOut ?? m?.netMoneyOut) ?? toNum(m?.MoneyOut ?? m?.moneyOut);
    const officialIn = toNum(verification.OfficialMoneyIn);
    const officialOut = toNum(verification.OfficialMoneyOut);
    const isAccumulated =
      isVerified &&
      officialIn != null &&
      officialOut != null &&
      currentOfficialIn != null &&
      currentOfficialOut != null &&
      (officialIn !== currentOfficialIn || officialOut !== currentOfficialOut);
    const explicitBaseIn = toNum(verification.BaseCounterIn ?? verification.PreviousCounterIn);
    const explicitBaseOut = toNum(verification.BaseCounterOut ?? verification.PreviousCounterOut);
    const inferredBaseIn =
      toNum(verification.CurrentCounterIn) != null && toNum(verification.NetCounterIn) != null
        ? toNum(verification.CurrentCounterIn) - toNum(verification.NetCounterIn)
        : null;
    const inferredBaseOut =
      toNum(verification.CurrentCounterOut) != null && toNum(verification.NetCounterOut) != null
        ? toNum(verification.CurrentCounterOut) - toNum(verification.NetCounterOut)
        : null;
    const accumulatedOfficialIn =
      toNum(verification.AccumulatedOfficialMoneyIn) ??
      (isAccumulated && officialIn != null && toNum(verification.CurrentOfficialMoneyIn) != null
        ? officialIn - toNum(verification.CurrentOfficialMoneyIn)
        : null);
    const accumulatedOfficialOut =
      toNum(verification.AccumulatedOfficialMoneyOut) ??
      (isAccumulated && officialOut != null && toNum(verification.CurrentOfficialMoneyOut) != null
        ? officialOut - toNum(verification.CurrentOfficialMoneyOut)
        : null);
    return {
      verification,
      formulaInfo: this.getCounterReadFormulaInfo(verification),
      status,
      reason,
      isVerified,
      isAccumulated,
      statusText,
      badgeStatus,
      note:
        verification.Note == null || String(verification.Note).trim() === ""
          ? null
          : String(verification.Note).trim(),
      baseIn: explicitBaseIn ?? inferredBaseIn,
      baseOut: explicitBaseOut ?? inferredBaseOut,
      CurrentOfficialMoneyIn: toNum(verification.CurrentOfficialMoneyIn),
      CurrentOfficialMoneyOut: toNum(verification.CurrentOfficialMoneyOut),
      AccumulatedOfficialMoneyIn: accumulatedOfficialIn,
      AccumulatedOfficialMoneyOut: accumulatedOfficialOut,
      AccumulatedOfficialBreakdown: Array.isArray(verification.AccumulatedOfficialBreakdown)
        ? verification.AccumulatedOfficialBreakdown
        : [],
    };
  }

  buildPostResetCell({ key, field, value, hasFormula, formula }) {
    const rawValue = this.isEdit ? this.userInputs?.[key]?.[field] : value;
    const numValue = this.toNumberOrNull(rawValue);
    const moneyEq =
      hasFormula && numValue != null
        ? this.toNumberOrNull(calculateFormula(numValue, formula))
        : null;
    const primaryDisplay = hasFormula
      ? this.creditFmt(numValue)
      : numValue == null
        ? "-"
        : this.moneyFmt.format(numValue);
    const primaryLabel = "Actual:";
    const moneyLabel = "Actual:";
    const secondaryRowStyle = {
      ...this.rowLine,
      opacity: 0.85,
    };
    return (
      <div
        style={{
          marginTop: 10,
          paddingTop: this.individualEditorLayout ? 0 : 10,
          borderTop: this.individualEditorLayout || this.collectionDetailPresentation
            ? undefined
            : "1px dashed rgba(115, 128, 150, 0.35)",
        }}
      >
        <div
          style={{
            fontWeight: 600,
            marginBottom: 6,
            opacity: 0.9,
            textAlign: this.individualEditorLayout ? "center" : undefined,
          }}
        >
          {this.individualEditorLayout || this.collectionDetailPresentation
            ? "Reset"
            : "Valores post-reset"}
        </div>
        {this.isEdit ? (
          <div style={secondaryRowStyle}>
            {this.NumericInput({
              fullWidthLine: true,
              id: `post-reset-${field}-${key}`,
              name: `post-reset-${field}-${key}`,
              label: primaryLabel,
              keyId: key,
              field,
              money: !hasFormula,
            })}
          </div>
        ) : (
          <div style={secondaryRowStyle}>
            <span>{primaryLabel}</span>
            <span>{primaryDisplay}</span>
          </div>
        )}
        {hasFormula && !this.individualEditorLayout && !this.collectionDetailPresentation && (
          <div style={secondaryRowStyle}>
            <span>{moneyLabel}</span>
            <span>{moneyEq != null ? this.moneyFmt.format(moneyEq) : "-"}</span>
          </div>
        )}
      </div>
    );
  }

  buildTotalCell({ m, moneyTotal, creditsTotal = null, hasFormula = false }) {
    if (this.individualEditorLayout) {
      return (
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: 8,
            width: "100%",
            fontWeight: 700,
          }}
        >
          <span>Total:</span>
          <span
            style={{
              ...(m?.collectedStyle || {}),
              ...this.negStyle(moneyTotal),
              fontWeight: 700,
            }}
          >
            {moneyTotal != null ? this.moneyFmt.format(moneyTotal) : "Pendiente"}
          </span>
        </div>
      );
    }

    return hasFormula ? (
      <div>
        {!this.collectionDetailPresentation && (
          <div style={this.rowLine}>
            <span>Recaudación:</span>
            <span
              style={{
                ...(m?.collectedStyle || {}),
                ...this.negStyle(creditsTotal),
              }}
            >
              {this.creditFmt(creditsTotal, "Pendiente")}
            </span>
          </div>
        )}
        <div style={this.rowLine}>
          <span>{this.collectionDetailPresentation ? "Total:" : "Recaudación:"}</span>
          <span
            style={{
              ...(m?.collectedStyle || {}),
              ...this.negStyle(moneyTotal),
              fontWeight: 700,
            }}
          >
            {moneyTotal != null ? this.moneyFmt.format(moneyTotal) : "Pendiente"}
          </span>
        </div>
      </div>
    ) : (
      <div>
        <div style={this.rowLine}>
          <span>{this.collectionDetailPresentation ? "Total:" : "Recaudación:"}</span>
          <span
            style={{
              ...(m?.collectedStyle || {}),
              fontWeight: 700,
              ...this.negStyle(moneyTotal),
            }}
          >
            {moneyTotal != null ? this.moneyFmt.format(moneyTotal) : "Pendiente"}
          </span>
        </div>
      </div>
    );
  }

  buildCells(m) {
    const key = this.computeKey(m);
    const last = this.getLastVisit(key) || {};
    const formula = this.getFormula(m);
    const hasFormula = this.usesFormula(m);
    const formulaName = this.getFormulaName(m);
    const mainTextStyle = { fontSize: this.individualEditorLayout ? 18 : 14 };
    const mainCell = (children) => <div style={mainTextStyle}>{children}</div>;

    const isAcc =
      typeof m?.IsAccumulative === "boolean"
        ? m.IsAccumulative
        : m?.IsAccumulative != null
          ? Boolean(m.IsAccumulative)
          : null;
    const accumText = getAccumulationModeLabel(isAcc);
    const baseFormulaText = this.collectionDetailPresentation
      ? hasFormula
        ? formulaName
        : "$"
      : this.individualEditorLayout
        ? hasFormula
          ? formulaName
          : "$"
        : `Fórmula: ${formulaName ?? "sin fórmula"}`;
    const conciseModeText =
      (this.individualEditorLayout || this.collectionDetailPresentation) && isAcc === false
        ? "Borrado"
        : null;
    const formulaText = this.collectionDetailPresentation
      ? [conciseModeText, baseFormulaText].filter(Boolean).join(" · ")
      : this.individualEditorLayout
        ? [conciseModeText, baseFormulaText].filter(Boolean).join(" ")
        : [baseFormulaText, accumText].filter(Boolean).join(" · ");

    if (isInsertedInCurrentVisitNoticeRow(m)) {
      const noticeText =
        m?.IsPendingInitialValuesNoticeRow === true
          ? "pendiente de valores iniciales"
          : "máquina ingresada en la visita actual";
      const machineCell = this.MachineCell({
        m,
        hasRent: false,
        formulaText: (
          <div>
            <div>{formulaText}</div>
            <div>{noticeText}</div>
          </div>
        ),
        machineActions: null,
        showWasCollectedToggle: false,
      });
      const inValue = hasFormula
        ? this.creditFmt(m?.InitialValuesIn, "-")
        : this.toNumberOrNull(m?.InitialValuesIn) == null
          ? "-"
          : this.moneyFmt.format(Number(m.InitialValuesIn));
      const outValue = hasFormula
        ? this.creditFmt(m?.InitialValuesOut, "-")
        : this.toNumberOrNull(m?.InitialValuesOut) == null
          ? "-"
          : this.moneyFmt.format(Number(m.InitialValuesOut));

      return {
        __useFullWidthCells: true,
        Machine: mainCell(machineCell),
        In: mainCell(
          <div>
            <div style={this.rowLine}>
              <span>
                {this.isRead || this.individualEditorLayout ? "Actual:" : "Acumulado actual:"}
              </span>
            </div>
            <div style={this.rowLine}>
              <span>{inValue}</span>
            </div>
          </div>
        ),
        Out: mainCell(
          <div>
            <div style={this.rowLine}>
              <span>
                {this.isRead || this.individualEditorLayout ? "Actual:" : "Acumulado actual:"}
              </span>
            </div>
            <div style={this.rowLine}>
              <span>{outValue}</span>
            </div>
          </div>
        ),
        Total: mainCell(<div />),
        Note: mainCell(<div />),
      };
    }

    const hasReset = Boolean(this.pick(m, "HasReset", "hasReset"));
    const canToggleReset = this.isEdit;
    const showPostResetBlock = hasReset;

    const isCollected = this.isWasCollected(m, key);
    const counterContext = this.getCounterContext(key);
    const counterReadDisplay = this.getCounterReadDisplay(m);
    const hasCounterVerificationUi =
      (this.isEdit && isCollected && counterContext) || (this.isRead && counterReadDisplay);
    const isCounterExpanded = hasCounterVerificationUi;
    const toggleCounterButton = null;
    const editCounterPreview =
      this.isEdit &&
      isCounterExpanded &&
      counterContext &&
      ["verify", "counter_reset"].includes(this.getCounterMode(key))
        ? this.calculateCounterPreview({ key, m, context: counterContext })
        : null;
    const editCounterMode = this.getCounterMode(key);
    const isCounterNotRead = editCounterMode === "not_read";
    const isCounterReset = editCounterMode === "counter_reset";
    const editCounterFormulaInfo = counterContext
      ? this.getCounterFormulaInfo(counterContext)
      : null;
    const readCounterFormulaInfo = counterReadDisplay?.formulaInfo || null;
    const activeCounterFormulaInfo = this.isEdit ? editCounterFormulaInfo : readCounterFormulaInfo;
    const sameCounterFormula = activeCounterFormulaInfo?.hasSameFormula === true;
    const readVerification = counterReadDisplay?.verification || null;
    const readHasCompleteCounterComparison =
      counterReadDisplay?.isVerified === true ||
      (counterReadDisplay?.reason === "counter_reset" &&
        readVerification?.CurrentCounterIn != null &&
        readVerification?.CurrentCounterOut != null &&
        readVerification?.NetCounterIn != null &&
        readVerification?.NetCounterOut != null);
    const readHasCounterInComparison =
      readHasCompleteCounterComparison ||
      (counterReadDisplay?.reason === "counter_reset" &&
        readVerification?.CurrentCounterIn != null &&
        readVerification?.NetCounterIn != null);
    const readHasCounterOutComparison =
      readHasCompleteCounterComparison ||
      (counterReadDisplay?.reason === "counter_reset" &&
        readVerification?.CurrentCounterOut != null &&
        readVerification?.NetCounterOut != null);
    const readShowsCounterIn =
      readHasCounterInComparison || counterReadDisplay?.reason === "counter_reset";
    const readShowsCounterOut =
      readHasCounterOutComparison || counterReadDisplay?.reason === "counter_reset";
    const editAccumulatedContext = this.isEdit
      ? this.getAccumulatedVerificationContext(counterContext)
      : null;
    const isCounterAccumulated = Boolean(
      editCounterPreview?.isAccumulated ||
        editAccumulatedContext ||
        counterReadDisplay?.isAccumulated
    );
    const includedVisitCount =
      editCounterPreview?.includedVisitCount ?? editAccumulatedContext?.includedVisitCount ?? null;
    const accumulatedVisitDatesText = this.formatAccumulatedVisitDates(
      editCounterPreview?.includedVisitDates ?? editAccumulatedContext?.includedVisitDates ?? []
    );
    const accumulatedExplanationText = this.buildAccumulatedCounterText({
      count: includedVisitCount,
      datesText: Number(includedVisitCount) > 1 ? accumulatedVisitDatesText : null,
    });
    const counterNumber = (value) => (value == null ? "-" : this.nf.format(value));
    const counterMoney = (value) => (value == null ? "-" : this.moneyFmt.format(value));
    const counterBaseInForDisplay =
      editCounterPreview?.BaseCounterIn ??
      editAccumulatedContext?.baseCounterIn ??
      counterContext?.previousBase?.CounterIn;
    const counterBaseOutForDisplay =
      editCounterPreview?.BaseCounterOut ??
      editAccumulatedContext?.baseCounterOut ??
      counterContext?.previousBase?.CounterOut;
    const counterInput = ({ field, label, disabled = false }) =>
      this.NumericInput({
        fullWidthLine: true,
        id: `counter-${field}-${key}`,
        name: `counter-${field}-${key}`,
        label,
        keyId: key,
        field,
        disabled,
        warning:
          field === "CounterCurrentIn"
            ? this.shouldShowAccumulatedValueWarning({
                key,
                field,
                currentValue: this.userInputs?.[key]?.CounterCurrentIn,
                previousValue: counterBaseInForDisplay,
                isCollected,
              })
            : field === "CounterCurrentOut"
              ? this.shouldShowAccumulatedValueWarning({
                  key,
                  field,
                  currentValue: this.userInputs?.[key]?.CounterCurrentOut,
                  previousValue: counterBaseOutForDisplay,
                  isCollected,
                })
              : false,
        warningText: this.accumulatedWarningText,
      });
    const counterToggleButtonStyle = (disabled = false) => ({
      border: "1px solid #b7bcc5",
      borderRadius: 6,
      background: "#fff",
      padding: "4px 8px",
      cursor: disabled ? "not-allowed" : "pointer",
      fontSize: this.individualEditorLayout ? 16 : 12,
      opacity: disabled ? 0.55 : 1,
      width: this.individualEditorLayout ? "100%" : undefined,
    });
    const counterModeButtons =
      this.isEdit && isCounterExpanded ? (
        <div
          style={
            this.individualEditorLayout
              ? {
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: 6,
                  width: "100%",
                }
              : { display: "grid", justifyItems: "start", gap: 6 }
          }
        >
          <button
            type="button"
            className={this.individualEditorLayout ? "individual-editor-action" : undefined}
            style={counterToggleButtonStyle(isCounterNotRead ? false : isCounterReset)}
            disabled={isCounterReset}
            onClick={() => {
              const nextMode = isCounterNotRead ? "verify" : "not_read";
              if (nextMode === "not_read") {
                this.handleInputChange(key, "CounterCurrentIn", "");
                this.handleInputChange(key, "CounterCurrentOut", "");
              }
              this.handleInputChange(key, "CounterMode", nextMode);
            }}
          >
            {isCounterNotRead ? "Leer contadores" : "No leer contadores"}
          </button>
          <button
            type="button"
            className={this.individualEditorLayout ? "individual-editor-action" : undefined}
            style={counterToggleButtonStyle(isCounterReset ? false : isCounterNotRead)}
            disabled={isCounterNotRead}
            onClick={() => {
              this.handleInputChange(
                key,
                "CounterMode",
                isCounterReset ? "verify" : "counter_reset"
              );
            }}
          >
            {isCounterReset ? "Deshacer reset" : "Reset"}
          </button>
        </div>
      ) : null;

    const resetBadge = hasReset ? (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          padding: "2px 8px",
          borderRadius: 999,
          fontSize: this.individualEditorLayout ? 16 : 11,
          fontWeight: 700,
          color: "#8a4b00",
          background: "#fff4df",
          border: "1px solid #f0d49a",
        }}
      >
        Reseteada
      </span>
    ) : null;

    const wasCollectedButton = this.individualEditorLayout
      ? this.renderWasCollectedButton(m, key, { width: "100%" })
      : null;
    const machineActionColumn = (
      <div
        style={
          this.individualEditorLayout
            ? {
                marginTop: 8,
                display: "grid",
                gridTemplateColumns:
                  wasCollectedButton && canToggleReset
                    ? "repeat(2, minmax(0, 1fr))"
                    : "minmax(0, 1fr)",
                gap: 6,
                width: "100%",
              }
            : { marginTop: 8, display: "grid", justifyItems: "start", gap: 6 }
        }
      >
        {wasCollectedButton}
        {canToggleReset && (
          <button
            type="button"
            className={this.individualEditorLayout ? "individual-editor-action" : undefined}
            style={{
              border: "1px solid #b7bcc5",
              borderRadius: 6,
              background: "#fff",
              padding: "4px 8px",
              cursor: "pointer",
              fontSize: this.individualEditorLayout ? 16 : 12,
              width: this.individualEditorLayout ? "100%" : undefined,
            }}
            disabled={!isCollected}
            onClick={() => this.handleToggleReset(m)}
          >
            {hasReset ? "Deshacer reset" : "Resetear"}
          </button>
        )}
        {toggleCounterButton}
      </div>
    );

    const machineCell = (
      <div>
        {this.MachineCell({
          m,
          hasRent: false,
          formulaText,
          machineActions:
            this.individualEditorLayout || this.collectionDetailPresentation ? null : resetBadge,
          plainStatusText: this.collectionDetailPresentation && hasReset ? "Reset" : null,
          showWasCollectedToggle: !this.individualEditorLayout,
        })}
        {machineActionColumn}
      </div>
    );
    const noteCell = this.NoteCell({ keyId: key, m });

    if (!isCollected && this.isRead) {
      return {
        __useFullWidthCells: true,
        Machine: mainCell(machineCell),
        In: mainCell(<div>-</div>),
        Out: mainCell(<div>-</div>),
        Total: mainCell(<div>-</div>),
        Note: mainCell(noteCell),
      };
    }

    let inCell = null;
    let outCell = null;
    let totalCell = null;
    const boldValueStyle = { fontWeight: 700 };
    const officialCurrentLabel =
      this.isRead || this.individualEditorLayout ? "Actual" : "Acumulado actual";
    const officialPreviousLabel =
      isAcc === false
        ? "Anterior"
        : this.isRead || this.individualEditorLayout
          ? "Anterior"
          : "Acumulado anterior";
    const officialCurrentLabelWithColon = `${officialCurrentLabel}:`;
    const officialPreviousLabelWithColon = `${officialPreviousLabel}:`;
    const previousLine = (value, suffix = "") => (
      <div style={this.rowLine} data-reading="previous">
        <span>
          {(this.individualEditorLayout || this.collectionDetailPresentation) && suffix
            ? `${officialPreviousLabel}${suffix}:`
            : officialPreviousLabelWithColon}
        </span>
        <span>{`${value}${
          this.individualEditorLayout || this.collectionDetailPresentation ? "" : suffix
        }`}</span>
      </div>
    );

    if (!hasFormula) {
      const netMoneyIn = this.pickLiveNumeric(m, "netMoneyIn", "NetMoneyIn");
      const netMoneyOut = this.pickLiveNumeric(m, "netMoneyOut", "NetMoneyOut");
      const moneyIn = this.pickLiveNumeric(m, "moneyIn", "MoneyIn");
      const moneyOut = this.pickLiveNumeric(m, "moneyOut", "MoneyOut");
      const moneyTotal = this.pickLiveNumeric(m, "moneyCollected", "MoneyCollected");
      const postResetIn = this.pick(m, "PostResetStartIn", "postResetStartIn");
      const postResetOut = this.pick(m, "PostResetStartOut", "postResetStartOut");

      if (this.isRead) {
        const prevMoneyInSuffix = this.getPrevDateSuffix(m?.PrevVisitId, m?.PrevVisitDate);
        const prevMoneyOutSuffix = this.getPrevDateSuffix(m?.PrevVisitId, m?.PrevVisitDate);
        if (isAcc === false) {
          const previousNetMoneyIn = this.toNumberOrNull(m?.PrevNetMoneyIn);
          const previousNetMoneyOut = this.toNumberOrNull(m?.PrevNetMoneyOut);
          inCell = (
            <div>
              {previousLine(
                previousNetMoneyIn != null ? this.moneyFmt.format(previousNetMoneyIn) : "—",
                previousNetMoneyIn != null ? prevMoneyInSuffix : ""
              )}
              <div style={this.rowLine}>
                <span>
                  {this.individualEditorLayout || this.collectionDetailPresentation
                    ? "Actual:"
                    : "Neto:"}
                </span>
                <span style={boldValueStyle}>
                  {netMoneyIn != null ? this.moneyFmt.format(netMoneyIn) : "-"}
                </span>
              </div>
              {showPostResetBlock &&
                isCollected &&
                this.buildPostResetCell({
                  key,
                  field: "PostResetStartIn",
                  value: postResetIn,
                  hasFormula,
                  formula,
                })}
            </div>
          );
          outCell = (
            <div>
              {previousLine(
                previousNetMoneyOut != null ? this.moneyFmt.format(previousNetMoneyOut) : "—",
                previousNetMoneyOut != null ? prevMoneyOutSuffix : ""
              )}
              <div style={this.rowLine}>
                <span>
                  {this.individualEditorLayout || this.collectionDetailPresentation
                    ? "Actual:"
                    : "Neto:"}
                </span>
                <span style={boldValueStyle}>
                  {netMoneyOut != null ? this.moneyFmt.format(netMoneyOut) : "-"}
                </span>
              </div>
              {showPostResetBlock &&
                isCollected &&
                this.buildPostResetCell({
                  key,
                  field: "PostResetStartOut",
                  value: postResetOut,
                  hasFormula,
                  formula,
                })}
            </div>
          );
        } else {
          inCell = (
            <div>
              {this.collectionDetailPresentation &&
                previousLine(
                  m.PrevMoneyIn != null ? this.moneyFmt.format(m.PrevMoneyIn) : "-",
                  m.PrevMoneyIn != null ? prevMoneyInSuffix : ""
                )}
              {this.fieldLine(officialCurrentLabel, this.moneyFmt.format(moneyIn || 0))}
              {!this.collectionDetailPresentation &&
                this.fieldLine(
                  officialPreviousLabel,
                  m.PrevMoneyIn != null
                    ? `${this.moneyFmt.format(m.PrevMoneyIn)}${prevMoneyInSuffix}`
                    : "-"
                )}
              {netMoneyIn != null &&
                this.fieldLine("Neto", this.moneyFmt.format(netMoneyIn), { boldValue: true })}
              {showPostResetBlock &&
                isCollected &&
                this.buildPostResetCell({
                  key,
                  field: "PostResetStartIn",
                  value: postResetIn,
                  hasFormula,
                  formula,
                })}
            </div>
          );
          outCell = (
            <div>
              {this.collectionDetailPresentation &&
                previousLine(
                  m.PrevMoneyOut != null ? this.moneyFmt.format(m.PrevMoneyOut) : "-",
                  m.PrevMoneyOut != null ? prevMoneyOutSuffix : ""
                )}
              {this.fieldLine(officialCurrentLabel, this.moneyFmt.format(moneyOut || 0))}
              {!this.collectionDetailPresentation &&
                this.fieldLine(
                  officialPreviousLabel,
                  m.PrevMoneyOut != null
                    ? `${this.moneyFmt.format(m.PrevMoneyOut)}${prevMoneyOutSuffix}`
                    : "-"
                )}
              {netMoneyOut != null &&
                this.fieldLine("Neto", this.moneyFmt.format(netMoneyOut), { boldValue: true })}
              {showPostResetBlock &&
                isCollected &&
                this.buildPostResetCell({
                  key,
                  field: "PostResetStartOut",
                  value: postResetOut,
                  hasFormula,
                  formula,
                })}
            </div>
          );
        }
      } else {
        const prevMoneyInValue =
          last.MoneyIn ?? last.PrevMoneyIn ?? m.lastMoneyIn ?? m.PrevMoneyIn ?? null;
        const prevMoneyOutValue =
          last.MoneyOut ?? last.PrevMoneyOut ?? m.lastMoneyOut ?? m.PrevMoneyOut ?? null;
        const prevMoneyIn = prevMoneyInValue != null ? this.moneyFmt.format(prevMoneyInValue) : "-";
        const prevMoneyOut =
          prevMoneyOutValue != null ? this.moneyFmt.format(prevMoneyOutValue) : "-";
        const prevMoneyInSuffix =
          prevMoneyIn === "-"
            ? ""
            : this.getPrevDateSuffix(
                last?.PrevVisitId ?? m?.PrevVisitId,
                last?.PrevVisitDate ?? m?.PrevVisitDate
              );
        const prevMoneyOutSuffix =
          prevMoneyOut === "-"
            ? ""
            : this.getPrevDateSuffix(
                last?.PrevVisitId ?? m?.PrevVisitId,
                last?.PrevVisitDate ?? m?.PrevVisitDate
              );
        if (isAcc === false) {
          const previousNetMoneyIn = this.toNumberOrNull(
            this.pickExplicitPreviousNet(last, m, "PrevNetMoneyIn")
          );
          const previousNetMoneyOut = this.toNumberOrNull(
            this.pickExplicitPreviousNet(last, m, "PrevNetMoneyOut")
          );

          inCell = (
            <div>
              <div style={this.rowLine} data-reading="previous">
                <span>{officialPreviousLabelWithColon}</span>
                <span>
                  {previousNetMoneyIn != null
                    ? `${this.moneyFmt.format(previousNetMoneyIn)}${prevMoneyInSuffix}`
                    : "—"}
                </span>
              </div>
              {this.NumericInput({
                fullWidthLine: true,
                id: `netMoneyIn-${key}`,
                name: `netMoneyIn-${key}`,
                label: this.individualEditorLayout ? "Actual:" : "Neto:",
                keyId: key,
                field: "NetMoneyIn",
                money: true,
                inputStyleOverride: boldValueStyle,
              })}
              {showPostResetBlock &&
                isCollected &&
                this.buildPostResetCell({
                  key,
                  field: "PostResetStartIn",
                  value: postResetIn,
                  hasFormula,
                  formula,
                })}
            </div>
          );

          outCell = (
            <div>
              <div style={this.rowLine} data-reading="previous">
                <span>{officialPreviousLabelWithColon}</span>
                <span>
                  {previousNetMoneyOut != null
                    ? `${this.moneyFmt.format(previousNetMoneyOut)}${prevMoneyOutSuffix}`
                    : "—"}
                </span>
              </div>
              {this.NumericInput({
                fullWidthLine: true,
                id: `netMoneyOut-${key}`,
                name: `netMoneyOut-${key}`,
                label: this.individualEditorLayout ? "Actual:" : "Neto:",
                keyId: key,
                field: "NetMoneyOut",
                money: true,
                inputStyleOverride: boldValueStyle,
              })}
              {showPostResetBlock &&
                isCollected &&
                this.buildPostResetCell({
                  key,
                  field: "PostResetStartOut",
                  value: postResetOut,
                  hasFormula,
                  formula,
                })}
            </div>
          );
        } else {
          const moneyInWarning = this.shouldShowAccumulatedValueWarning({
            key,
            field: "MoneyIn",
            currentValue: this.userInputs?.[key]?.MoneyIn,
            previousValue: prevMoneyInValue,
            isCollected,
          });
          const moneyOutWarning = this.shouldShowAccumulatedValueWarning({
            key,
            field: "MoneyOut",
            currentValue: this.userInputs?.[key]?.MoneyOut,
            previousValue: prevMoneyOutValue,
            isCollected,
          });

          inCell = (
            <div>
              {this.individualEditorLayout && previousLine(prevMoneyIn, prevMoneyInSuffix)}
              {this.NumericInput({
                fullWidthLine: true,
                id: `moneyIn-${key}`,
                name: `moneyIn-${key}`,
                label: officialCurrentLabelWithColon,
                keyId: key,
                field: "MoneyIn",
                money: true,
                warning: moneyInWarning,
                warningText: this.accumulatedWarningText,
              })}
              {!this.individualEditorLayout && previousLine(prevMoneyIn, prevMoneyInSuffix)}
              <div style={this.rowLine}>
                <span>Neto:</span>
                <span style={boldValueStyle}>
                  {typeof netMoneyIn === "number" ? this.moneyFmt.format(netMoneyIn) : "Pendiente"}
                </span>
              </div>
              {showPostResetBlock &&
                isCollected &&
                this.buildPostResetCell({
                  key,
                  field: "PostResetStartIn",
                  value: postResetIn,
                  hasFormula,
                  formula,
                })}
            </div>
          );

          outCell = (
            <div>
              {this.individualEditorLayout && previousLine(prevMoneyOut, prevMoneyOutSuffix)}
              {this.NumericInput({
                fullWidthLine: true,
                id: `moneyOut-${key}`,
                name: `moneyOut-${key}`,
                label: officialCurrentLabelWithColon,
                keyId: key,
                field: "MoneyOut",
                money: true,
                warning: moneyOutWarning,
                warningText: this.accumulatedWarningText,
              })}
              {!this.individualEditorLayout && previousLine(prevMoneyOut, prevMoneyOutSuffix)}
              <div style={this.rowLine}>
                <span>Neto:</span>
                <span style={boldValueStyle}>
                  {typeof netMoneyOut === "number"
                    ? this.moneyFmt.format(netMoneyOut)
                    : "Pendiente"}
                </span>
              </div>
              {showPostResetBlock &&
                isCollected &&
                this.buildPostResetCell({
                  key,
                  field: "PostResetStartOut",
                  value: postResetOut,
                  hasFormula,
                  formula,
                })}
            </div>
          );
        }
      }

      totalCell = this.buildTotalCell({ m, moneyTotal });
    } else {
      const netIn = this.pickLiveNumeric(m, "netCreditsIn", "NetCreditsIn");
      const netOut = this.pickLiveNumeric(m, "netCreditsOut", "NetCreditsOut");
      const netMoneyIn = this.pickLiveNumeric(m, "netMoneyIn", "NetMoneyIn");
      const netMoneyOut = this.pickLiveNumeric(m, "netMoneyOut", "NetMoneyOut");
      const creditsTotal = this.pickLiveNumeric(m, "creditsCollected", "CreditsCollected");
      const moneyTotal = this.pickLiveNumeric(m, "moneyCollected", "MoneyCollected");
      const postResetIn = this.pick(m, "PostResetStartIn", "postResetStartIn");
      const postResetOut = this.pick(m, "PostResetStartOut", "postResetStartOut");

      if (this.isRead) {
        const prevCreditsInSuffix = this.getPrevDateSuffix(m?.PrevVisitId, m?.PrevVisitDate);
        const prevCreditsOutSuffix = this.getPrevDateSuffix(m?.PrevVisitId, m?.PrevVisitDate);
        if (isAcc === false) {
          const previousNetCreditsIn = this.toNumberOrNull(m?.PrevNetCreditsIn);
          const previousNetCreditsOut = this.toNumberOrNull(m?.PrevNetCreditsOut);
          inCell = (
            <div>
              {previousLine(
                previousNetCreditsIn != null ? this.creditFmt(previousNetCreditsIn) : "—",
                previousNetCreditsIn != null ? prevCreditsInSuffix : ""
              )}
              <div style={this.rowLine}>
                <span>
                  {this.individualEditorLayout || this.collectionDetailPresentation
                    ? "Actual:"
                    : "Neto:"}
                </span>
                <span>{this.creditFmt(netIn)}</span>
              </div>
              <div style={this.rowLine}>
                <span>
                  {this.individualEditorLayout || this.collectionDetailPresentation
                    ? "Actual:"
                    : "Neto:"}
                </span>
                <span style={boldValueStyle}>
                  {netMoneyIn != null ? this.moneyFmt.format(netMoneyIn) : "-"}
                </span>
              </div>
              {showPostResetBlock &&
                isCollected &&
                this.buildPostResetCell({
                  key,
                  field: "PostResetStartIn",
                  value: postResetIn,
                  hasFormula,
                  formula,
                })}
            </div>
          );
          outCell = (
            <div>
              {previousLine(
                previousNetCreditsOut != null ? this.creditFmt(previousNetCreditsOut) : "—",
                previousNetCreditsOut != null ? prevCreditsOutSuffix : ""
              )}
              <div style={this.rowLine}>
                <span>
                  {this.individualEditorLayout || this.collectionDetailPresentation
                    ? "Actual:"
                    : "Neto:"}
                </span>
                <span>{this.creditFmt(netOut)}</span>
              </div>
              <div style={this.rowLine}>
                <span>
                  {this.individualEditorLayout || this.collectionDetailPresentation
                    ? "Actual:"
                    : "Neto:"}
                </span>
                <span style={boldValueStyle}>
                  {netMoneyOut != null ? this.moneyFmt.format(netMoneyOut) : "-"}
                </span>
              </div>
              {showPostResetBlock &&
                isCollected &&
                this.buildPostResetCell({
                  key,
                  field: "PostResetStartOut",
                  value: postResetOut,
                  hasFormula,
                  formula,
                })}
            </div>
          );
        } else {
          inCell = (
            <div>
              {this.collectionDetailPresentation &&
                previousLine(
                  m.PrevCreditsIn != null ? this.creditFmt(m.PrevCreditsIn) : "-",
                  m.PrevCreditsIn != null ? prevCreditsInSuffix : ""
                )}
              {this.fieldLine(
                officialCurrentLabel,
                this.creditFmt(this.pick(m, "CreditsIn", "actualCreditsIn") || 0)
              )}
              {!this.collectionDetailPresentation &&
                this.fieldLine(
                  officialPreviousLabel,
                  m.PrevCreditsIn != null
                    ? `${this.creditFmt(m.PrevCreditsIn)}${prevCreditsInSuffix}`
                    : "-"
                )}
              {!this.collectionDetailPresentation &&
                netIn != null &&
                this.fieldLine("Neto", this.creditFmt(netIn))}
              {this.fieldLine("Neto", this.moneyFmt.format(netMoneyIn || 0), { boldValue: true })}
              {showPostResetBlock &&
                isCollected &&
                this.buildPostResetCell({
                  key,
                  field: "PostResetStartIn",
                  value: postResetIn,
                  hasFormula,
                  formula,
                })}
            </div>
          );
          outCell = (
            <div>
              {this.collectionDetailPresentation &&
                previousLine(
                  m.PrevCreditsOut != null ? this.creditFmt(m.PrevCreditsOut) : "-",
                  m.PrevCreditsOut != null ? prevCreditsOutSuffix : ""
                )}
              {this.fieldLine(
                officialCurrentLabel,
                this.creditFmt(this.pick(m, "CreditsOut", "actualCreditsOut") || 0)
              )}
              {!this.collectionDetailPresentation &&
                this.fieldLine(
                  officialPreviousLabel,
                  m.PrevCreditsOut != null
                    ? `${this.creditFmt(m.PrevCreditsOut)}${prevCreditsOutSuffix}`
                    : "-"
                )}
              {!this.collectionDetailPresentation &&
                netOut != null &&
                this.fieldLine("Neto", this.creditFmt(netOut))}
              {this.fieldLine("Neto", this.moneyFmt.format(netMoneyOut || 0), {
                boldValue: true,
              })}
              {showPostResetBlock &&
                isCollected &&
                this.buildPostResetCell({
                  key,
                  field: "PostResetStartOut",
                  value: postResetOut,
                  hasFormula,
                  formula,
                })}
            </div>
          );
        }
      } else {
        const prevIn =
          last.CreditsIn ?? last.PrevCreditsIn ?? m.lastCreditsIn ?? m.PrevCreditsIn ?? null;
        const prevOut =
          last.CreditsOut ?? last.PrevCreditsOut ?? m.lastCreditsOut ?? m.PrevCreditsOut ?? null;
        const prevInSuffix =
          prevIn == null
            ? ""
            : this.getPrevDateSuffix(
                last?.PrevVisitId ?? m?.PrevVisitId,
                last?.PrevVisitDate ?? m?.PrevVisitDate
              );
        const prevOutSuffix =
          prevOut == null
            ? ""
            : this.getPrevDateSuffix(
                last?.PrevVisitId ?? m?.PrevVisitId,
                last?.PrevVisitDate ?? m?.PrevVisitDate
              );

        if (isAcc === false) {
          const previousNetCreditsIn = this.toNumberOrNull(
            this.pickExplicitPreviousNet(last, m, "PrevNetCreditsIn")
          );
          const previousNetCreditsOut = this.toNumberOrNull(
            this.pickExplicitPreviousNet(last, m, "PrevNetCreditsOut")
          );
          inCell = (
            <div>
              {previousLine(
                previousNetCreditsIn != null ? this.creditFmt(previousNetCreditsIn) : "—",
                previousNetCreditsIn != null ? prevInSuffix : ""
              )}
              {this.NumericInput({
                fullWidthLine: true,
                id: `netCreditsIn-${key}`,
                name: `netCreditsIn-${key}`,
                label: this.individualEditorLayout ? "Actual:" : "Neto:",
                keyId: key,
                field: "NetCreditsIn",
              })}
              <div style={this.rowLine}>
                <span>{this.individualEditorLayout ? "Actual:" : "Neto:"}</span>
                <span style={boldValueStyle}>
                  {typeof netMoneyIn === "number" ? this.moneyFmt.format(netMoneyIn) : "Pendiente"}
                </span>
              </div>
              {showPostResetBlock &&
                isCollected &&
                this.buildPostResetCell({
                  key,
                  field: "PostResetStartIn",
                  value: postResetIn,
                  hasFormula,
                  formula,
                })}
            </div>
          );

          outCell = (
            <div>
              {previousLine(
                previousNetCreditsOut != null ? this.creditFmt(previousNetCreditsOut) : "—",
                previousNetCreditsOut != null ? prevOutSuffix : ""
              )}
              {this.NumericInput({
                fullWidthLine: true,
                id: `netCreditsOut-${key}`,
                name: `netCreditsOut-${key}`,
                label: this.individualEditorLayout ? "Actual:" : "Neto:",
                keyId: key,
                field: "NetCreditsOut",
              })}
              <div style={this.rowLine}>
                <span>{this.individualEditorLayout ? "Actual:" : "Neto:"}</span>
                <span style={boldValueStyle}>
                  {typeof netMoneyOut === "number"
                    ? this.moneyFmt.format(netMoneyOut)
                    : "Pendiente"}
                </span>
              </div>
              {showPostResetBlock &&
                isCollected &&
                this.buildPostResetCell({
                  key,
                  field: "PostResetStartOut",
                  value: postResetOut,
                  hasFormula,
                  formula,
                })}
            </div>
          );
        } else {
          const creditsInWarning = this.shouldShowAccumulatedValueWarning({
            key,
            field: "CreditsIn",
            currentValue: this.userInputs?.[key]?.CreditsIn,
            previousValue: prevIn,
            isCollected,
          });
          const creditsOutWarning = this.shouldShowAccumulatedValueWarning({
            key,
            field: "CreditsOut",
            currentValue: this.userInputs?.[key]?.CreditsOut,
            previousValue: prevOut,
            isCollected,
          });

          inCell = (
            <div>
              {this.individualEditorLayout &&
                previousLine(prevIn != null ? this.creditFmt(prevIn) : "-", prevInSuffix)}
              {this.NumericInput({
                fullWidthLine: true,
                id: `creditsIn-${key}`,
                name: `creditsIn-${key}`,
                label: officialCurrentLabelWithColon,
                keyId: key,
                field: "CreditsIn",
                warning: creditsInWarning,
                warningText: this.accumulatedWarningText,
              })}
              {!this.individualEditorLayout &&
                previousLine(prevIn != null ? this.creditFmt(prevIn) : "-", prevInSuffix)}
              {!this.individualEditorLayout && (
                <div style={this.rowLine}>
                  <span>Neto:</span>
                  <span>{this.creditFmt(netIn, "Pendiente")}</span>
                </div>
              )}
              <div style={this.rowLine}>
                <span>Neto:</span>
                <span style={boldValueStyle}>
                  {typeof netMoneyIn === "number" ? this.moneyFmt.format(netMoneyIn) : "Pendiente"}
                </span>
              </div>
              {showPostResetBlock &&
                isCollected &&
                this.buildPostResetCell({
                  key,
                  field: "PostResetStartIn",
                  value: postResetIn,
                  hasFormula,
                  formula,
                })}
            </div>
          );

          outCell = (
            <div>
              {this.individualEditorLayout &&
                previousLine(prevOut != null ? this.creditFmt(prevOut) : "-", prevOutSuffix)}
              {this.NumericInput({
                fullWidthLine: true,
                id: `creditsOut-${key}`,
                name: `creditsOut-${key}`,
                label: officialCurrentLabelWithColon,
                keyId: key,
                field: "CreditsOut",
                warning: creditsOutWarning,
                warningText: this.accumulatedWarningText,
              })}
              {!this.individualEditorLayout &&
                previousLine(prevOut != null ? this.creditFmt(prevOut) : "-", prevOutSuffix)}
              {!this.individualEditorLayout && (
                <div style={this.rowLine}>
                  <span>Neto:</span>
                  <span>{this.creditFmt(netOut, "Pendiente")}</span>
                </div>
              )}
              <div style={this.rowLine}>
                <span>Neto:</span>
                <span style={boldValueStyle}>
                  {typeof netMoneyOut === "number"
                    ? this.moneyFmt.format(netMoneyOut)
                    : "Pendiente"}
                </span>
              </div>
              {showPostResetBlock &&
                isCollected &&
                this.buildPostResetCell({
                  key,
                  field: "PostResetStartOut",
                  value: postResetOut,
                  hasFormula,
                  formula,
                })}
            </div>
          );
        }
      }

      totalCell = this.buildTotalCell({ m, moneyTotal, creditsTotal, hasFormula: true });
    }

    const pendingText = "Pendiente";
    const counterValue = (value, formatter) => (value == null ? pendingText : formatter(value));
    const doubleCellStyle = {
      ...mainTextStyle,
      paddingTop: 8,
      verticalAlign: "top",
    };
    const doubleCell = (children) => <div style={doubleCellStyle}>{children}</div>;
    const editCounterTolerance =
      this.toNumberOrNull(editCounterPreview?.tolerance) ??
      this.toNumberOrNull(counterContext?.toleranceAmount) ??
      this.toNumberOrNull(counterContext?.config?.ToleranceAmount);
    const readCounterTolerance =
      this.toNumberOrNull(readVerification?.ToleranceAmount) ??
      this.toNumberOrNull(counterReadDisplay?.ToleranceAmount);
    const readDisplayDifferenceIn = this.getDisplayMoneyDifference({
      officialMoney: readVerification?.OfficialMoneyIn,
      counterMoney: readVerification?.CounterMoneyIn,
      fallbackDifference: readVerification?.DifferenceMoneyIn,
    });
    const readDisplayDifferenceOut = this.getDisplayMoneyDifference({
      officialMoney: readVerification?.OfficialMoneyOut,
      counterMoney: readVerification?.CounterMoneyOut,
      fallbackDifference: readVerification?.DifferenceMoneyOut,
    });
    const hasReadDetailAccumulatedBreakdown = Boolean(
      this.collectionDetailPresentation &&
        this.isRead &&
        counterReadDisplay?.isAccumulated === true &&
        Array.isArray(counterReadDisplay.AccumulatedOfficialBreakdown) &&
        counterReadDisplay.AccumulatedOfficialBreakdown.length > 0
    );
    const editAccumulatedInBreakdown =
      !this.individualEditorLayout && editCounterPreview?.isAccumulated === true
        ? this.renderAccumulatedComparisonBreakdown({
            currentOfficialMoney: editCounterPreview.CurrentOfficialMoneyIn,
            accumulatedOfficialMoney: editCounterPreview.PersistedAccumulatedOfficialMoneyIn,
            includedVisitBreakdown: editCounterPreview.includedVisitBreakdown,
            side: "in",
            totalOfficialMoney: editCounterPreview.OfficialMoneyIn,
            counterMoney: editCounterPreview.CounterMoneyIn,
            difference: editCounterPreview.DifferenceMoneyIn,
          })
        : null;
    const editAccumulatedOutBreakdown =
      !this.individualEditorLayout && editCounterPreview?.isAccumulated === true
        ? this.renderAccumulatedComparisonBreakdown({
            currentOfficialMoney: editCounterPreview.CurrentOfficialMoneyOut,
            accumulatedOfficialMoney: editCounterPreview.PersistedAccumulatedOfficialMoneyOut,
            includedVisitBreakdown: editCounterPreview.includedVisitBreakdown,
            side: "out",
            totalOfficialMoney: editCounterPreview.OfficialMoneyOut,
            counterMoney: editCounterPreview.CounterMoneyOut,
            difference: editCounterPreview.DifferenceMoneyOut,
          })
        : null;
    const readAccumulatedInBreakdown =
      !this.individualEditorLayout &&
      (!this.collectionDetailPresentation || !hasReadDetailAccumulatedBreakdown) &&
      counterReadDisplay?.isAccumulated === true
        ? this.renderAccumulatedComparisonBreakdown({
            currentOfficialMoney: counterReadDisplay.CurrentOfficialMoneyIn,
            accumulatedOfficialMoney: counterReadDisplay.AccumulatedOfficialMoneyIn,
            includedVisitBreakdown: counterReadDisplay.AccumulatedOfficialBreakdown,
            side: "in",
            totalOfficialMoney: readVerification?.OfficialMoneyIn,
            counterMoney: readVerification?.CounterMoneyIn,
            difference: readVerification?.DifferenceMoneyIn,
          })
        : null;
    const readAccumulatedOutBreakdown =
      !this.individualEditorLayout &&
      (!this.collectionDetailPresentation || !hasReadDetailAccumulatedBreakdown) &&
      counterReadDisplay?.isAccumulated === true
        ? this.renderAccumulatedComparisonBreakdown({
            currentOfficialMoney: counterReadDisplay.CurrentOfficialMoneyOut,
            accumulatedOfficialMoney: counterReadDisplay.AccumulatedOfficialMoneyOut,
            includedVisitBreakdown: counterReadDisplay.AccumulatedOfficialBreakdown,
            side: "out",
            totalOfficialMoney: readVerification?.OfficialMoneyOut,
            counterMoney: readVerification?.CounterMoneyOut,
            difference: readVerification?.DifferenceMoneyOut,
          })
        : null;
    const editAccumulatedInRows =
      editCounterPreview?.isAccumulated === true
        ? this.renderAccumulatedCollectionRows({
            includedVisitBreakdown: editCounterPreview.includedVisitBreakdown,
            side: "in",
          })
        : [];
    const editAccumulatedOutRows =
      editCounterPreview?.isAccumulated === true
        ? this.renderAccumulatedCollectionRows({
            includedVisitBreakdown: editCounterPreview.includedVisitBreakdown,
            side: "out",
          })
        : [];
    const readAccumulatedInRows =
      counterReadDisplay?.isAccumulated === true
        ? this.renderAccumulatedCollectionRows({
            includedVisitBreakdown: counterReadDisplay.AccumulatedOfficialBreakdown,
            side: "in",
          })
        : [];
    const readAccumulatedOutRows =
      counterReadDisplay?.isAccumulated === true
        ? this.renderAccumulatedCollectionRows({
            includedVisitBreakdown: counterReadDisplay.AccumulatedOfficialBreakdown,
            side: "out",
          })
        : [];
    const readDetailAccumulatedInRows = this.renderCollectionDetailAccumulatedRows({
      includedVisitBreakdown: counterReadDisplay?.AccumulatedOfficialBreakdown,
      side: "in",
    });
    const readDetailAccumulatedOutRows = this.renderCollectionDetailAccumulatedRows({
      includedVisitBreakdown: counterReadDisplay?.AccumulatedOfficialBreakdown,
      side: "out",
    });
    const buildComparisonRows = ({
      current,
      accumulatedRows,
      detailAccumulatedRows = [],
      total,
      isAccumulated,
      side,
    }) => {
      const showCollectionDetailComparison =
        this.collectionDetailPresentation &&
        this.isRead &&
        isAccumulated &&
        detailAccumulatedRows.length > 0;
      if (!this.individualEditorLayout && !showCollectionDetailComparison) return [];
      const comparisonTitle = this.individualEditorLayout ? (
        this.counterGroupTitle("Comparativa")
      ) : (
        <div style={{ marginTop: 10, fontWeight: 600, marginBottom: 6, opacity: 0.9 }}>
          Comparativa
        </div>
      );
      const rows = [
        React.cloneElement(comparisonTitle, {
          key: `comparison-title-${side}`,
        }),
        React.cloneElement(this.counterLine("Actual", counterValue(current, counterMoney)), {
          key: `comparison-current-${side}`,
        }),
        ...(showCollectionDetailComparison ? detailAccumulatedRows : accumulatedRows),
      ];
      if (isAccumulated) {
        rows.push(
          React.cloneElement(this.counterLine("Total", counterValue(total, counterMoney)), {
            key: `comparison-total-${side}`,
          })
        );
      }
      return rows;
    };
    const editComparisonInRows = buildComparisonRows({
      current: editCounterPreview?.isAccumulated
        ? editCounterPreview.CurrentOfficialMoneyIn
        : editCounterPreview?.OfficialMoneyIn,
      accumulatedRows: editAccumulatedInRows,
      total: editCounterPreview?.OfficialMoneyIn,
      isAccumulated: editCounterPreview?.isAccumulated === true,
      side: "edit-in",
    });
    const editComparisonOutRows = buildComparisonRows({
      current: editCounterPreview?.isAccumulated
        ? editCounterPreview.CurrentOfficialMoneyOut
        : editCounterPreview?.OfficialMoneyOut,
      accumulatedRows: editAccumulatedOutRows,
      total: editCounterPreview?.OfficialMoneyOut,
      isAccumulated: editCounterPreview?.isAccumulated === true,
      side: "edit-out",
    });
    const readComparisonInRows = buildComparisonRows({
      current: counterReadDisplay?.isAccumulated
        ? counterReadDisplay.CurrentOfficialMoneyIn
        : readVerification?.OfficialMoneyIn,
      accumulatedRows: readAccumulatedInRows,
      detailAccumulatedRows: readDetailAccumulatedInRows,
      total: readVerification?.OfficialMoneyIn,
      isAccumulated: counterReadDisplay?.isAccumulated === true,
      side: "read-in",
    });
    const readComparisonOutRows = buildComparisonRows({
      current: counterReadDisplay?.isAccumulated
        ? counterReadDisplay.CurrentOfficialMoneyOut
        : readVerification?.OfficialMoneyOut,
      accumulatedRows: readAccumulatedOutRows,
      detailAccumulatedRows: readDetailAccumulatedOutRows,
      total: readVerification?.OfficialMoneyOut,
      isAccumulated: counterReadDisplay?.isAccumulated === true,
      side: "read-out",
    });
    const editDifferenceLabel =
      this.individualEditorLayout || editCounterPreview?.isAccumulated !== true
        ? "Diferencia"
        : "Diferencia acumulada";
    const readDifferenceLabel =
      this.individualEditorLayout ||
      this.collectionDetailPresentation ||
      counterReadDisplay?.isAccumulated !== true
        ? "Diferencia"
        : "Diferencia acumulada";
    const counterCurrentLabel = this.isRead
      ? !this.collectionDetailPresentation && counterReadDisplay?.reason === "counter_reset"
        ? "Actual pre-reset"
        : "Actual"
      : this.individualEditorLayout
        ? "Actual"
        : isCounterReset
          ? "Acumulado actual pre-reset"
          : "Acumulado actual";
    const counterPreviousLabel =
      this.isRead || this.individualEditorLayout ? "Anterior" : "Acumulado anterior";
    const counterFormulaFontSize = this.individualEditorLayout ? 16 : null;
    const editPostResetInput = (field, legacyLabel) =>
      this.individualEditorLayout ? (
        <div style={{ display: "grid", gap: 6, marginTop: 6 }}>
          <div style={{ fontWeight: 700, textAlign: "center" }}>Reset</div>
          {counterInput({ field, label: "Actual" })}
        </div>
      ) : (
        counterInput({ field, label: legacyLabel })
      );
    const readPostResetValue = (value, legacyLabel) =>
      this.individualEditorLayout ? (
        <div style={{ display: "grid", gap: 6, marginTop: 6 }}>
          <div style={{ fontWeight: 700, textAlign: "center" }}>Reset</div>
          {this.counterLine("Actual", counterNumber(value))}
        </div>
      ) : this.collectionDetailPresentation ? (
        <div style={{ marginTop: 10, paddingTop: 0 }}>
          <div style={{ fontWeight: 600, marginBottom: 6, opacity: 0.9 }}>Reset</div>
          {this.counterLine("Actual", counterNumber(value))}
        </div>
      ) : (
        this.counterLine(legacyLabel, counterNumber(value))
      );
    const editInBlock =
      this.isEdit && isCounterExpanded
        ? this.counterDisclosureBlock([
            !sameCounterFormula &&
              this.counterLine("Fórmula", activeCounterFormulaInfo?.inText, {
                fontSize: counterFormulaFontSize,
              }),
            !this.individualEditorLayout && this.counterGroupTitle("Contador"),
            counterInput({
              field: "CounterCurrentIn",
              label: counterCurrentLabel,
              disabled: isCounterNotRead,
            }),
            this.counterLine(
              counterPreviousLabel,
              counterValue(counterBaseInForDisplay, counterNumber)
            ),
            !this.individualEditorLayout &&
              !this.collectionDetailPresentation &&
              this.counterLine(
                "Neto",
                counterValue(editCounterPreview?.NetCounterIn, counterNumber)
              ),
            this.counterLine(
              "Neto",
              counterValue(editCounterPreview?.CounterMoneyIn, counterMoney),
              {
                boldValue: true,
              }
            ),
            ...editComparisonInRows,
            this.counterDifferenceLine(
              editDifferenceLabel,
              editCounterPreview?.DifferenceMoneyIn,
              counterMoney,
              editCounterTolerance
            ),
            editAccumulatedInBreakdown,
            isCounterReset && editPostResetInput("CounterPostResetIn", "Post reset entrada"),
          ])
        : null;
    const editOutBlock =
      this.isEdit && isCounterExpanded
        ? this.counterDisclosureBlock([
            !sameCounterFormula &&
              this.counterLine("Fórmula", activeCounterFormulaInfo?.outText, {
                fontSize: counterFormulaFontSize,
              }),
            !this.individualEditorLayout && this.counterGroupTitle("Contador"),
            counterInput({
              field: "CounterCurrentOut",
              label: counterCurrentLabel,
              disabled: isCounterNotRead,
            }),
            this.counterLine(
              counterPreviousLabel,
              counterValue(counterBaseOutForDisplay, counterNumber)
            ),
            !this.individualEditorLayout &&
              !this.collectionDetailPresentation &&
              this.counterLine(
                "Neto",
                counterValue(editCounterPreview?.NetCounterOut, counterNumber)
              ),
            this.counterLine(
              "Neto",
              counterValue(editCounterPreview?.CounterMoneyOut, counterMoney),
              {
                boldValue: true,
              }
            ),
            ...editComparisonOutRows,
            this.counterDifferenceLine(
              editDifferenceLabel,
              editCounterPreview?.DifferenceMoneyOut,
              counterMoney,
              editCounterTolerance
            ),
            editAccumulatedOutBreakdown,
            isCounterReset && editPostResetInput("CounterPostResetOut", "Post reset salida"),
          ])
        : null;
    const readInBlock =
      this.isRead && isCounterExpanded && counterReadDisplay
        ? this.counterDisclosureBlock([
            !sameCounterFormula &&
              this.counterLine("Fórmula", activeCounterFormulaInfo?.inText, {
                fontSize: counterFormulaFontSize,
              }),
            !this.individualEditorLayout && this.counterGroupTitle("Contador"),
            (readShowsCounterIn || this.individualEditorLayout) &&
              this.counterLine(
                counterCurrentLabel,
                counterValue(readVerification.CurrentCounterIn, counterNumber)
              ),
            (counterReadDisplay.baseIn != null || this.individualEditorLayout) &&
              this.counterLine(
                counterPreviousLabel,
                counterValue(counterReadDisplay.baseIn, counterNumber)
              ),
            !this.individualEditorLayout &&
              !this.collectionDetailPresentation &&
              readShowsCounterIn &&
              this.counterLine("Neto", counterValue(readVerification.NetCounterIn, counterNumber)),
            (readShowsCounterIn || this.individualEditorLayout) &&
              this.counterLine(
                "Neto",
                counterValue(readVerification.CounterMoneyIn, counterMoney),
                {
                  boldValue: true,
                }
              ),
            ...readComparisonInRows,
            (readShowsCounterIn || this.individualEditorLayout) &&
              this.counterDifferenceLine(
                readDifferenceLabel,
                readDisplayDifferenceIn,
                counterMoney,
                readCounterTolerance
              ),
            readAccumulatedInBreakdown,
            counterReadDisplay.reason === "counter_reset" &&
              readPostResetValue(readVerification.PostResetCounterIn, "Post reset entrada"),
          ])
        : null;
    const readOutBlock =
      this.isRead && isCounterExpanded && counterReadDisplay
        ? this.counterDisclosureBlock([
            !sameCounterFormula &&
              this.counterLine("Fórmula", activeCounterFormulaInfo?.outText, {
                fontSize: counterFormulaFontSize,
              }),
            !this.individualEditorLayout && this.counterGroupTitle("Contador"),
            (readShowsCounterOut || this.individualEditorLayout) &&
              this.counterLine(
                counterCurrentLabel,
                counterValue(readVerification.CurrentCounterOut, counterNumber)
              ),
            (counterReadDisplay.baseOut != null || this.individualEditorLayout) &&
              this.counterLine(
                counterPreviousLabel,
                counterValue(counterReadDisplay.baseOut, counterNumber)
              ),
            !this.individualEditorLayout &&
              !this.collectionDetailPresentation &&
              readShowsCounterOut &&
              this.counterLine("Neto", counterValue(readVerification.NetCounterOut, counterNumber)),
            (readShowsCounterOut || this.individualEditorLayout) &&
              this.counterLine(
                "Neto",
                counterValue(readVerification.CounterMoneyOut, counterMoney),
                {
                  boldValue: true,
                }
              ),
            ...readComparisonOutRows,
            (readShowsCounterOut || this.individualEditorLayout) &&
              this.counterDifferenceLine(
                readDifferenceLabel,
                readDisplayDifferenceOut,
                counterMoney,
                readCounterTolerance
              ),
            readAccumulatedOutBreakdown,
            counterReadDisplay.reason === "counter_reset" &&
              readPostResetValue(readVerification.PostResetCounterOut, "Post reset salida"),
          ])
        : null;
    const editStatusBadge =
      this.isEdit && isCounterExpanded
        ? isCounterReset
          ? this.counterStatusBadge("Contador reiniciado", "warning")
          : editCounterPreview?.statusText
            ? this.counterStatusBadge(
                this.individualEditorLayout &&
                  editCounterPreview.statusText === "Dentro de tolerancia"
                  ? "En rango"
                  : editCounterPreview.statusText,
                editCounterPreview.statusColor === "#b31d28" ? "error" : "success"
              )
            : null
        : null;
    const readStatusBadge =
      this.isRead &&
      isCounterExpanded &&
      counterReadDisplay &&
      (counterReadDisplay.status !== "unverified" || counterReadDisplay.reason === "counter_reset")
        ? this.collectionDetailPresentation
          ? counterReadDisplay.reason === "counter_reset"
            ? counterPlainStatusText("Reset")
            : counterReadDisplay.isVerified
              ? null
              : this.counterStatusBadge(
                  counterReadDisplay.statusText,
                  counterReadDisplay.badgeStatus
                )
          : this.counterStatusBadge(
              counterReadDisplay.reason === "counter_reset"
                ? "Contador reiniciado"
                : this.individualEditorLayout &&
                    counterReadDisplay.statusText === "Dentro de tolerancia"
                  ? "En rango"
                  : counterReadDisplay.statusText,
              counterReadDisplay.badgeStatus
            )
        : null;
    const readCounterReasonBlock =
      this.isRead && isCounterExpanded && counterReadDisplay?.reason === "not_read"
        ? this.collectionDetailPresentation
          ? counterPlainStatusText("Sin lectura")
          : this.counterParagraph("No se pudieron leer los contadores")
        : null;
    function counterPlainStatusText(text) {
      return (
        <div
          style={{
            fontSize: 12,
            fontStyle: "normal",
            fontWeight: 400,
            opacity: 0.85,
            lineHeight: 1.25,
          }}
        >
          {text}
        </div>
      );
    }
    const counterMachineMetaLine = (label, value) => {
      if (value == null || value === "") return null;
      return (
        <div
          style={{
            fontSize: this.individualEditorLayout ? 16 : 12,
            fontStyle: this.collectionDetailPresentation ? "normal" : "italic",
            opacity: 0.9,
            lineHeight: 1.25,
            textAlign: this.individualEditorLayout ? "center" : "left",
          }}
        >
          {label}: {value}
        </div>
      );
    };
    const activeCounterTolerance = this.isEdit ? editCounterTolerance : readCounterTolerance;
    const individualCounterMeta = this.individualEditorLayout
      ? sameCounterFormula
        ? counterMachineMetaLine(
            "Fórmula",
            `${activeCounterFormulaInfo?.inText ?? "-"}${
              activeCounterTolerance != null
                ? ` · Tolerancia: ${counterMoney(activeCounterTolerance)}`
                : ""
            }`
          )
        : activeCounterTolerance != null
          ? counterMachineMetaLine("Tolerancia", counterMoney(activeCounterTolerance))
          : null
      : null;
    const editNoteBlock =
      this.isEdit && isCounterExpanded
        ? this.counterDisclosureBlock(
            this.individualEditorLayout ? (
              <AutoGrowingNoteTextarea
                aria-label="Nota de contadores"
                placeholder="Añadir nota sobre contadores…"
                value={this.userInputs?.[key]?.CounterNote ?? ""}
                onChange={(e) => this.handleInputChange(key, "CounterNote", e.target.value)}
                style={{
                  ...this.textInputStyle,
                  width: "100%",
                  whiteSpace: "normal",
                  overflowWrap: "break-word",
                  lineHeight: 1.35,
                  font: "inherit",
                  boxSizing: "border-box",
                }}
              />
            ) : (
              <textarea
                placeholder="Nota de contadores"
                value={this.userInputs?.[key]?.CounterNote ?? ""}
                onChange={(e) => this.handleInputChange(key, "CounterNote", e.target.value)}
                style={{
                  ...this.textInputStyle,
                  width: "100%",
                  minHeight: 80,
                  resize: "vertical",
                  whiteSpace: "normal",
                  overflowWrap: "break-word",
                  lineHeight: 1.35,
                  font: "inherit",
                  boxSizing: "border-box",
                }}
              />
            )
          )
        : null;
    const readNoteBlock =
      this.isRead && isCounterExpanded && counterReadDisplay
        ? this.counterDisclosureBlock(
            <div
              style={{
                width: "100%",
                minHeight: 80,
                whiteSpace: "pre-wrap",
                overflowWrap: "break-word",
                wordBreak: "break-word",
                lineHeight: 1.35,
              }}
            >
              {counterReadDisplay.note || "-"}
            </div>
          )
        : null;

    // Only the individual editor and collection detail use the new reading order.
    // The first two visible lines form the Previous/Current pair in these cells.
    const currentFirst = (cell) => {
      if (!cell || (!this.individualEditorLayout && !this.collectionDetailPresentation)) {
        return cell;
      }
      const lines = React.Children.toArray(cell.props.children);
      if (lines.length < 2 || lines[0]?.props?.["data-reading"] !== "previous") {
        return cell;
      }
      return React.cloneElement(cell, undefined, lines[1], lines[0], ...lines.slice(2));
    };

    const normalRow = {
      __useFullWidthCells: true,
      Machine: mainCell(machineCell),
      In: mainCell(currentFirst(inCell)),
      Out: outCell ? mainCell(currentFirst(outCell)) : undefined,
      Total: totalCell ? mainCell(totalCell) : undefined,
      Note: mainCell(noteCell),
    };
    if (!isCounterExpanded) return normalRow;

    return [
      normalRow,
      {
        __useFullWidthCells: true,
        __isCounterVerificationDetailRow: true,
        Machine: doubleCell(
          this.counterDisclosureBlock(
            [
              <div
                key="title"
                style={{ fontWeight: 800, fontSize: this.individualEditorLayout ? 20 : undefined }}
              >
                {this.individualEditorLayout ? "Doble verificación con contadores" : "Contadores"}
              </div>,
              !this.individualEditorLayout && (this.isEdit ? editStatusBadge : readStatusBadge),
              !this.individualEditorLayout &&
                this.collectionDetailPresentation &&
                sameCounterFormula &&
                counterMachineMetaLine("Fórmula", activeCounterFormulaInfo?.inText),
              !this.individualEditorLayout &&
                this.isEdit &&
                editCounterTolerance != null &&
                counterMachineMetaLine("Tolerancia", counterMoney(editCounterTolerance)),
              !this.individualEditorLayout &&
                this.isRead &&
                readCounterTolerance != null &&
                counterMachineMetaLine("Tolerancia", counterMoney(readCounterTolerance)),
              !this.individualEditorLayout &&
                !this.collectionDetailPresentation &&
                sameCounterFormula &&
                counterMachineMetaLine("Fórmula", activeCounterFormulaInfo?.inText),
              individualCounterMeta,
              counterModeButtons,
              isCounterAccumulated &&
                !this.individualEditorLayout &&
                !hasReadDetailAccumulatedBreakdown && (
                <React.Fragment key="accumulated-explanation">
                  {this.counterParagraph(accumulatedExplanationText)}
                </React.Fragment>
              ),
              readCounterReasonBlock,
            ],
            { centered: this.individualEditorLayout }
          )
        ),
        In: doubleCell(this.isEdit ? editInBlock : readInBlock),
        Out: doubleCell(this.isEdit ? editOutBlock : readOutBlock),
        Total: doubleCell(null),
        Note: doubleCell(this.isEdit ? editNoteBlock : readNoteBlock),
      },
    ];
  }
}
